import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 min

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const timerRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem("aurora_token");
    setUser(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (localStorage.getItem("aurora_token")) {
        logout();
        // toast import-free notice
        try {
          window.dispatchEvent(new CustomEvent("aurora:session-expired"));
        } catch (_err) {
          /* noop */
        }
      }
    }, SESSION_TIMEOUT_MS);
  }, [logout]);

  useEffect(() => {
    const token = localStorage.getItem("aurora_token");
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        resetTimer();
      })
      .catch(() => {
        localStorage.removeItem("aurora_token");
        setUser(false);
      });
  }, [resetTimer]);

  // Track user activity — reset timer on interaction
  useEffect(() => {
    if (!user || user === false) return;
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    const onActivity = () => resetTimer();
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user, resetTimer]);

  const login = async (email, password, otp, turnstileToken) => {
    const res = await api.post("/auth/login", {
      email,
      password,
      otp,
      turnstile_token: turnstileToken,
    });
    if (res.data.require_2fa) return { require_2fa: true };
    localStorage.setItem("aurora_token", res.data.access_token);
    setUser(res.data.user);
    resetTimer();
    return res.data.user;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
